import { Body, Button, Heading } from '@assembly-js/design-system';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  PERM_MOD_SETTINGS_PLANS,
  PLAN_PAGE,
  PLANS_OPERATIONS,
} from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import history from 'src/history';
import { useIsProxySession } from 'src/hooks/useIsProxySession';
import { useAppSelector } from 'src/hooks/useStore';
import { ModalWrapper } from 'src/legacy/components/Modals';
import {
  useGetChurnAccessQuery,
  useGetCurrentPlanQuery,
} from 'src/services/api/studioCreditApi';
import * as UserUtils from 'src/utils/UserUtils';

import churnLockHeader from './assets/churn-lock-header.svg';

/**
 * Locks the dashboard after a paid plan ends by cancellation or failed
 * payments. The plan picker lives on the plans page, so this stays closed
 * there. An intentional downgrade to Freemium never reports blocked.
 * The churn-access-lock flag keeps this closed until the lock is turned on.
 */
export const ChurnAccessModal: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = React.useContext(RouteContext);
  const { EnableChurnAccessLock } = useFeatureFlags();

  const userLoaded = useAppSelector((state) => state.user.loaded);
  const isClient = useAppSelector((state) => state.user.isClient);
  const userId = useAppSelector((state) => state.user.id);
  const isProxySession = useIsProxySession(userId);
  const permissions = useAppSelector((state) => state.user.data?.permissions);
  const canSubscribe = UserUtils.hasOperation(
    PERM_MOD_SETTINGS_PLANS,
    PLANS_OPERATIONS.UpgradePlan,
    permissions,
  );

  const { data: currentPlan } = useGetCurrentPlanQuery(undefined, {
    skip: !userLoaded || isClient,
  });
  const onFreemium =
    currentPlan?.family === 'studio' && currentPlan.plan === 'freemium';
  const { data: churnAccess } = useGetChurnAccessQuery(undefined, {
    skip: !EnableChurnAccessLock || !onFreemium || isProxySession,
  });
  const onPlansPage =
    pathname === PLAN_PAGE.path || pathname.startsWith(`${PLAN_PAGE.path}/`);
  const open =
    EnableChurnAccessLock &&
    Boolean(churnAccess?.blocked) &&
    onFreemium &&
    !isProxySession &&
    !onPlansPage;

  const lastAttempt = churnAccess?.lastAttempt;
  const paymentBrand = churnAccess?.paymentMethodBrand;
  const last4 = churnAccess?.paymentMethodLast4;

  return (
    <ModalWrapper
      closeButton={false}
      disableEscapeKeyDown
      hasBackdrop
      hideDividers
      hideActions
      denseContent
      noBorder
      width={canSubscribe ? 480 : 380}
      open={open}
    >
      <div className="flex flex-col">
        <div className="flex h-[191px] items-center justify-center bg-black">
          <img src={churnLockHeader} alt="" />
        </div>
        <div className="flex flex-col gap-5 px-6 pb-6 pt-6">
          <div className="flex flex-col gap-2">
            <Heading
              size="xl"
              className="mb-0! text-[20px]! leading-[28px]! text-black-heading"
            >
              {t(($) => $.page_studio_plans.churn_access.title)}
            </Heading>
            <Body size="sm" className="text-gray-small">
              {t(($) =>
                canSubscribe
                  ? $.page_studio_plans.churn_access.body
                  : $.page_studio_plans.churn_access.body_staff,
              )}
            </Body>
          </div>
          {canSubscribe && (lastAttempt || (paymentBrand && last4)) && (
            <div className="flex flex-col gap-3 rounded border border-solid border-non-hover-border px-3 py-4">
              {lastAttempt ? (
                <div className="flex items-start justify-between gap-8">
                  <Body
                    size="base"
                    className="cop-font-medium text-black-heading"
                  >
                    {t(($) => $.page_studio_plans.churn_access.last_attempt)}
                  </Body>
                  <Body size="base" className="shrink-0 text-black-heading">
                    {moment.unix(lastAttempt).format('D MMM YYYY')}
                  </Body>
                </div>
              ) : null}
              {lastAttempt && paymentBrand && last4 ? (
                <div className="h-px w-full bg-non-hover-border" />
              ) : null}
              {paymentBrand && last4 ? (
                <div className="flex items-start justify-between gap-8">
                  <Body
                    size="base"
                    className="cop-font-medium text-black-heading"
                  >
                    {t(($) => $.page_studio_plans.churn_access.payment_method)}
                  </Body>
                  <Body size="base" className="shrink-0 text-black-heading">
                    {t(($) => $.page_studio_plans.churn_access.card_ending, {
                      brand: paymentBrand,
                      last4: '',
                    }).trimEnd()}{' '}
                    <span className="cop-font-medium">{last4}</span>
                  </Body>
                </div>
              ) : null}
            </div>
          )}
          {canSubscribe && (
            <Button
              label={t(($) => $.page_studio_plans.churn_access.select_plan)}
              variant="primary"
              className="w-full"
              onClick={() => history.push(PLAN_PAGE.path)}
            />
          )}
          <Button
            label={t(($) => $.page_studio_plans.churn_access.switch_workspace)}
            variant="secondary"
            className="w-full"
            onClick={() => history.push('/add-account')}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};
